import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Check, Lock, FileSpreadsheet, FileText, Moon, CalendarRange, PartyPopper, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import { useApi, errMsg } from "../lib/api";

const FEATURES = [
  { icon: FileSpreadsheet, title: "CSV export", text: "Send your month to a spreadsheet or your accountant." },
  { icon: FileText, title: "PDF timesheet", text: "A clean monthly report to check against your payslip." },
  { icon: Moon, title: "Night shift bonus", text: "Extra % for hours worked between set night hours." },
  { icon: CalendarRange, title: "Weekend bonus", text: "Separate rates for Saturdays and Sundays." },
  { icon: PartyPopper, title: "Holiday bonus", text: "Public holiday surcharges calculated for you." },
];

// Stripe pages refuse to load inside frames (e.g. the preview panel), so open a new tab there.
export const openStripe = (url) => {
  if (window.self !== window.top) window.open(url, "_blank", "noopener");
  else window.location.href = url;
};

const fmtDate = (iso) => (iso ? format(parseISO(iso), "d MMMM yyyy") : "");

const ManageButton = ({ label = "Manage subscription" }) => {
  const api = useApi();
  const portal = useMutation({
    mutationFn: () => api("post", "/billing/portal", { origin_url: window.location.origin }),
    onSuccess: (r) => openStripe(r.url),
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <button onClick={() => portal.mutate()} disabled={portal.isPending} data-testid="premium-manage-btn"
      className="mt-6 w-full rounded-full border border-border bg-background/40 py-3.5 font-bold transition-colors hover:bg-secondary disabled:opacity-60">
      {portal.isPending ? "Opening…" : label}
    </button>
  );
};

const ActivePanel = ({ sub }) => (
  <div data-testid="premium-active-panel">
    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-bold uppercase text-emerald-300">
      <ShieldCheck className="h-3.5 w-3.5" /> Premium active
    </div>
    <div className="mt-5 flex items-baseline gap-2">
      <span className="font-heading text-6xl font-black tracking-tight">€3.99</span>
      <span className="text-muted-foreground">/ month</span>
    </div>
    <p data-testid="premium-renewal-text" className="mt-4 text-sm text-muted-foreground">
      {sub.cancel_at_period_end
        ? `Cancelled — Premium stays active until ${fmtDate(sub.current_period_end)}.`
        : `Renews on ${fmtDate(sub.current_period_end)}.`}
    </p>
    <ManageButton />
  </div>
);

const UpgradePanel = ({ sub }) => {
  const api = useApi();
  const checkout = useMutation({
    mutationFn: () => api("post", "/billing/checkout", { origin_url: window.location.origin }),
    onSuccess: (r) => openStripe(r.checkout_url),
    onError: (e) => toast.error(errMsg(e)),
  });
  const failed = sub.status === "past_due" || sub.status === "unpaid";
  return (
    <div>
      {failed && (
        <div data-testid="premium-payment-failed" className="mb-5 flex gap-2 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Your last payment failed. Update your card to get Premium back.
        </div>
      )}
      <div className="flex items-baseline gap-2">
        <span data-testid="premium-price" className="font-heading text-6xl font-black tracking-tight">€3.99</span>
        <span className="text-muted-foreground">/ month</span>
      </div>
      <ul className="mt-6 space-y-3 text-sm">
        {["Everything in Free", "Unlimited CSV & PDF exports", "Night, weekend & holiday bonuses", "Cancel anytime"].map((t) => (
          <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" />{t}</li>
        ))}
      </ul>
      {failed ? <ManageButton label="Update payment method" /> : (
        <button onClick={() => checkout.mutate()} disabled={checkout.isPending} data-testid="premium-upgrade-btn"
          className="mt-8 w-full rounded-full bg-amber-500 py-3.5 font-bold text-[#0B0F17] transition-transform hover:-translate-y-0.5 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60">
          {checkout.isPending ? "Opening secure checkout…" : "Upgrade – €3.99/month"}
        </button>
      )}
      <p className="mt-3 text-center text-xs text-muted-foreground">Secure payment by Stripe. Cancel anytime.</p>
    </div>
  );
};

export default function Premium() {
  const api = useApi();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [refresh] = useState(params.get("portal") === "1");
  const { data: sub } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => api("get", `/billing/subscription${refresh ? "?refresh=true" : ""}`),
  });
  useEffect(() => {
    const onFocus = () => qc.invalidateQueries({ queryKey: ["subscription"] });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [qc]);

  return (
    <div data-testid="premium-page" className="space-y-8">
      <div>
        <p className="inline-flex items-center gap-2 font-num text-xs font-bold uppercase tracking-wider text-amber-500"><Sparkles className="h-3.5 w-3.5" />ShiftMate Premium</p>
        <h1 className="mt-1 font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">Exports & advanced pay</h1>
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="grid gap-3 sm:grid-cols-2 lg:col-span-7">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-border/70 bg-card p-5">
              <div className="flex items-center justify-between">
                <Icon className="h-5 w-5 text-amber-500" />
                {!sub?.premium && <Lock className="h-4 w-4 text-muted-foreground" />}
              </div>
              <h3 className="mt-4 font-bold">{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
        <div className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent p-6 sm:p-8">
            {!sub ? <div className="h-48 animate-pulse rounded-xl bg-card" /> : sub.premium ? <ActivePanel sub={sub} /> : <UpgradePanel sub={sub} />}
          </div>
        </div>
      </div>
    </div>
  );
}
